import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { PaymentReceiptService } from "services/payment-receipt";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { numberToWords, getPrintTemplate } from "./components/printTemplate";

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const composeDate = (parts) => {
  if (!hasAnyDatePart(parts)) return "";
  const day = parts.day.padStart(2, "0");
  const month = parts.month.padStart(2, "0");
  const year = parts.year;
  return `${day}/${month}/${year}`;
};

const validateDateParts = (parts, label) => {
  if (!hasAnyDatePart(parts)) return "";
  if (!hasAllDatePart(parts)) throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (month < 1 || month > 12) throw new Error(`${label} có tháng không hợp lệ`);
  if (year < 1900 || year > 9999) throw new Error(`${label} có năm không hợp lệ`);
  const maxDays = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDays) throw new Error(`${label} có ngày không hợp lệ`);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

export default function PaymentReceiptDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [header, setHeader] = useState({
    unit: "",
    address: "",
    bookNumber: "",
    documentNumber: "",
    payerName: "",
    payerAddress: "",
    content: "",
    amount: "",
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await PaymentReceiptService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          address: data.address || "",
          bookNumber: data.bookNumber || "",
          documentNumber: data.documentNumber || "",
          payerName: data.payerName || "",
          payerAddress: data.payerAddress || "",
          content: data.content || "",
          amount: formatVndIntegerInput(data.amount || ""),
        });

        setDocumentDateParts(splitDateParts(data.documentDate || ""));
      } catch (error) {
        toast.error("Không thể tải dữ liệu biên lai thu tiền");
      }
    })();
  }, [id]);

  const derivedAmount = useMemo(() => roundMoney(header.amount), [header.amount]);
  const derivedAmountText = useMemo(
    () => (derivedAmount > 0 ? numberToWords(derivedAmount) : ""),
    [derivedAmount]
  );

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDocumentDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setDocumentDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const address = String(header.address || "").trim();
    const documentDate = validateDateParts(documentDateParts, "Ngày lập biên lai");
    const bookNumber = String(header.bookNumber || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const payerName = String(header.payerName || "").trim();
    const payerAddress = String(header.payerAddress || "").trim();
    const content = String(header.content || "").trim();
    const amount = derivedAmount;
    const amountText = String(derivedAmountText || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!address) throw new Error("Địa chỉ không được để trống");
    if (!documentDate) throw new Error("Ngày lập biên lai không được để trống");
    if (!documentNumber) throw new Error("Số biên lai không được để trống");
    if (!payerName) throw new Error("Họ tên người nộp không được để trống");
    if (!payerAddress) throw new Error("Địa chỉ không được để trống");
    if (!content) throw new Error("Nội dung thu không được để trống");
    if (amount <= 0) throw new Error("Số tiền thu phải lớn hơn 0");
    if (!amountText) throw new Error("Số tiền bằng chữ không được để trống");

    return {
      unit,
      address,
      documentDate,
      bookNumber,
      documentNumber,
      payerName,
      payerAddress,
      content,
      amount,
      amountText,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await PaymentReceiptService.create(payload);
        toast.success("Tạo biên lai thu tiền thành công");
      } else {
        await PaymentReceiptService.update(id, payload);
        toast.success("Lưu biên lai thu tiền thành công");
      }
      navigation("/templates/payment-receipt");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        amountText: derivedAmountText,
        documentDate: composeDate(documentDateParts),
        amount: derivedAmount,
      },
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card style={s.container}>
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <SoftBox>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Địa chỉ:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.address}
                        onChange={(event) => setHeaderField("address", event.target.value)}
                      />
                    </div>
                  </SoftBox>

                  <SoftBox textAlign="center">
                    <div>
                      <b>Mẫu số 06 - TT</b>
                    </div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>(Kèm theo Thông tư số 99/2025/TT-BTC</div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
                  </SoftBox>
                </SoftBox>

                <SoftBox textAlign="center" mt={3} mb={2}>
                  <div style={{ fontWeight: 700, fontSize: 22 }}>BIÊN LAI THU TIỀN</div>
                  <div style={{ fontStyle: "italic", marginTop: 4 }}>
                    Ngày{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.day}
                      onChange={(event) => setDateField("day", event.target.value)}
                      onBlur={() => padDateField("day")}
                    />{" "}
                    tháng{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.month}
                      onChange={(event) => setDateField("month", event.target.value)}
                      onBlur={() => padDateField("month")}
                    />{" "}
                    năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 60 }}
                      value={documentDateParts.year}
                      onChange={(event) => setDateField("year", event.target.value)}
                    />
                  </div>
                </SoftBox>

                <SoftBox display="flex" justifyContent="flex-end" mt={1} mb={2}>
                  <div style={{ lineHeight: 1.8 }}>
                    <div>
                      Quyển số:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 160 }}
                        value={header.bookNumber}
                        onChange={(event) => setHeaderField("bookNumber", event.target.value)}
                      />
                    </div>
                    <div>
                      Số:{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 160 }}
                        value={header.documentNumber}
                        onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                      />
                    </div>
                  </div>
                </SoftBox>

                <div style={{ marginTop: 12, lineHeight: 1.8 }}>
                  <div>
                    - Họ và tên người nộp:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 420 }}
                      value={header.payerName}
                      onChange={(event) => setHeaderField("payerName", event.target.value)}
                    />
                  </div>
                  <div>
                    - Địa chỉ:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 420 }}
                      value={header.payerAddress}
                      onChange={(event) => setHeaderField("payerAddress", event.target.value)}
                    />
                  </div>
                  <div>
                    - Nội dung thu:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 420 }}
                      value={header.content}
                      onChange={(event) => setHeaderField("content", event.target.value)}
                    />
                  </div>
                  <div>
                    - Số tiền thu:{" "}
                    <input
                      disabled={containView}
                      inputMode="numeric"
                      style={{ ...s.inputDotted, width: 180, textAlign: "right" }}
                      value={header.amount}
                      onChange={(event) => setHeaderField("amount", formatVndIntegerInput(event.target.value))}
                    />{" "}
                    (Viết bằng chữ):{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 360 }}
                      value={derivedAmountText}
                      readOnly
                    />
                  </div>
                </div>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="outlined" color="secondary" onClick={() => navigation("/templates/payment-receipt")}>TRỞ VỀ</SoftButton>
                  <SoftBox display="flex" gap={1}>
                    {containView && (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        IN BẢNG
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {loadingSubmit ? "ĐANG LƯU..." : id ? "LƯU" : "TẠO"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
